import { useMemo } from 'react';
import { useNavigate, useQueryState, useQuery, useI18n, useMutation, toast } from '@ywwwtseng/react-kit';
import {
  Account,
  Typography,
  openWebLink,
} from '@ywwwtseng/tma-react-kit';
import * as schema from '@/db/schema';
import {
  Navigation,
  ShieldCheck,
  Users,
  ChevronRight,
  Languages,
  HandCoins,
  Award,
  Wallet,
} from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { RedPackage } from '@/components/icons';
import { BADGE_LEVELS } from '@/constants';

const languages = [
  // { name: 'English', key: 'en' },
  // { name: '中文', key: 'zh' },
  { name: 'English (US)', key: 'en-US' },
  { name: '简体中文', key: 'zh-CN' },
  { name: '繁體中文', key: 'zh-TW' },
  { name: 'العربية', key: 'ar-SA' },
  { name: 'বাংলা', key: 'bn-BD' },
  { name: 'Čeština', key: 'cs-CZ' },
  { name: 'Dansk', key: 'da-DK' },
  { name: 'Deutsch', key: 'de-DE' },
  { name: 'Ελληνικά', key: 'el-GR' },
  { name: 'فارسی', key: 'fa-IR' },
  { name: 'Suomi', key: 'fi-FI' },
  { name: 'Français', key: 'fr-FR' },
  { name: 'עברית', key: 'he-IL' },
  { name: 'हिन्दी', key: 'hi-IN' },
  { name: 'Magyar', key: 'hu-HU' },
  { name: 'Bahasa Indonesia', key: 'id-ID' },
  { name: 'Italiano', key: 'it-IT' },
  { name: '日本語', key: 'ja-JP' },
  { name: '한국어', key: 'ko-KR' },
  { name: 'Nederlands', key: 'nl-NL' },
  { name: 'Norsk', key: 'no-NO' },
  { name: 'Polski', key: 'pl-PL' },
  { name: 'Português (Brasil)', key: 'pt-BR' },
  { name: 'Português (Portugal)', key: 'pt-PT' },
  { name: 'Română', key: 'ro-RO' },
  { name: 'Русский', key: 'ru-RU' },
  { name: 'Svenska', key: 'sv-SE' },
  { name: 'ไทย', key: 'th-TH' },
  { name: 'Türkçe', key: 'tr-TR' },
  { name: 'Українська', key: 'uk-UA' },
  { name: 'اردو', key: 'ur-PK' },
  { name: 'Tiếng Việt', key: 'vi-VN' },
];

export default function MyScreen() {
  const { t, localeCode, setLocaleCode } = useI18n();
  const { mutate, isLoading } = useMutation('me:update');

  const setLocale = (code: string) => {
    if (isLoading) {
      toast(t('message.language_synchronizing'));
    };
    setLocaleCode(code);
    void mutate({
      language_code: code,
    });
  };

  const lang = useMemo(() => {
    return (
      languages.find((lang) => lang.key === localeCode) ??
      languages[0]
    );
  }, [localeCode]);

  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const { data: logs } = useQuery<
    (typeof schema.user_activity_logs.$inferSelect)[]
  >('user_activity_logs', {
    refetchOnMount: true,
    params: {
      type: 'like:gift:%',
    },
  });
  const { data: totalReferred } = useQuery<string>('total_referred', {
    enabled: !!me,
  });

  const totalSend = useMemo(() => {
    return logs?.find((log) => log.type === 'gift:total_send')?.amount ?? '0';
  }, [logs]);

  const totalReceived = useMemo(() => {
    return (
      logs?.find((log) => log.type === 'gift:total_received')?.amount ?? '0'
    );
  }, [logs]);

  const badgeLevel = useMemo(() => {
    if (!me?.badge_level) return 1;
    return me.badge_level as keyof typeof BADGE_LEVELS;
  }, [me?.badge_level]);

  const badgeInfo = useMemo(() => {
    return BADGE_LEVELS[badgeLevel] || BADGE_LEVELS[1];
  }, [badgeLevel]);

  const badgeName = useMemo(() => {
    return t(`common.badge_level_${badgeLevel}` as any) || badgeInfo.name;
  }, [badgeLevel, badgeInfo.name, t]);

  const navigate = useNavigate();

  if (!me) {
    return undefined;
  }

  return (
    <div className="flex flex-col items-center justify-start w-full gap-5 py-3 px-5">
      <div className="card flex flex-col items-center w-full gap-2 !justify-start">
        <div className="h-stack w-full gap-2">
          <Account.Avatar size={48} />
          <div className="v-stack w-full !items-start">
            <div className="h-stack gap-2 items-center">
              <Typography size="5" weight={600}>
                {me.tg_last_name
                  ? `${me.tg_last_name} ${me.tg_first_name}`
                  : me.tg_first_name}
              </Typography>
              <div className="h-stack gap-1 items-center px-2 py-0.5 rounded-full bg-primary/20">
                <Award size={14} className="text-primary" />
                <Typography size="1" weight={500} className="text-primary">
                  Lv{badgeLevel} {badgeName}
                </Typography>
              </div>
            </div>
            {me.tg_username && (
              <Typography size="2" color="rgba(255,255,255,0.54)">
                {`@${me.tg_username}`}
              </Typography>
            )}
          </div>
        </div>
        <div className="h-stack w-full">
          <div className="h-stack w-full gap-1 py-1 pr-2">
            <Typography size="2" weight={500} i18n="common.total_send" />
            <Typography size="2" weight={500} className="ml-auto">
              {totalSend}
            </Typography>
            <RedPackage width={14} height={14} />
          </div>
          <div className="h-stack w-full gap-1 py-1 pr-2">
            <Typography size="2" weight={500} i18n="common.total_received" />
            <Typography size="2" weight={500} className="ml-auto">
              {totalReceived}
            </Typography>
            <RedPackage width={14} height={14} />
          </div>
        </div>

        <div className="h-stack w-full pr-2 gap-1">
          <Typography size="2" weight={500} i18n="common.total_referred" />
          <Typography size="2" weight={500} className="ml-auto">
            {totalReferred || '0'}
          </Typography>
          <Users width={14} height={14} />
        </div>
      </div>

      <div className="card v-stack w-full">
        <div
          className="h-stack w-full gap-2 px-1 py-2 border-b border-border"
          onClick={() => {
            navigate('GroupChannel');
          }}
        >
          <div className="bg-destructive rounded-lg p-1">
            <Users width={18} height={18} />
          </div>
          <Typography
            size="3"
            weight={500}
            i18n="screens.group_channel_settings"
          />
          <ChevronRight
            className="ml-auto text-icon-foreground"
            size={24}
            strokeWidth={1.5}
          />
        </div>

        <Select
          trigger={
            <div className="h-stack w-full gap-2 px-1 py-2 border-b border-border">
              <div className="bg-primary rounded-lg p-1">
                <Languages size={18} strokeWidth={3} />
              </div>
              <Typography size="3" weight={500} i18n="settings.language" />
              <div className="ml-auto">
                <Typography size="2" weight={500}>
                  {lang?.name}
                </Typography>
              </div>
            </div>
          }
          options={languages}
          onChange={setLocale}
        />
        <div
          className="h-stack w-full gap-2 px-1 py-2 border-b border-border"
          onClick={() => {
            navigate('AccountActivity');
          }}
        >
          <div className="bg-destructive rounded-lg p-1">
            <Navigation size={18} strokeWidth={3} />
          </div>
          <Typography size="3" weight={500} i18n="settings.account_activity" />
          <ChevronRight
            className="ml-auto text-icon-foreground"
            size={24}
            strokeWidth={1.5}
          />
        </div>
        <div
          className="h-stack w-full gap-2 px-1 py-2 border-b border-border"
          onClick={() => {
            navigate('WalletBindingRecords');
          }}
        >
          <div className="bg-primary rounded-lg p-1">
            <Wallet size={18} strokeWidth={2} />
          </div>
          <Typography size="3" weight={500} i18n="screens.wallet_binding_records" />
          <ChevronRight
            className="ml-auto text-icon-foreground"
            size={24}
            strokeWidth={1.5}
          />
        </div>
        <div
          className="h-stack w-full gap-2 px-1 py-2 border-b border-border"
          onClick={() => {
            navigate('Investor');
          }}
        >
          <div className="bg-primary rounded-lg p-1">
            <HandCoins size={18} strokeWidth={3} />
          </div>
          <Typography size="3" weight={500} i18n="investor.title" />
          <ChevronRight
            className="ml-auto text-icon-foreground"
            size={24}
            strokeWidth={1.5}
          />
        </div>
        <div
          className="h-stack w-full gap-2 px-1 py-2"
          onClick={() => {
            openWebLink(`https://moonpacket.com/${localeCode}/privacy/`);
          }}
        >
          <div className="bg-warning rounded-lg p-1">
            <ShieldCheck size={18} strokeWidth={3} />
          </div>
          <Typography size="3" weight={500} i18n="settings.privacy_policy" />
          <ChevronRight
            className="ml-auto text-icon-foreground"
            size={24}
            strokeWidth={1.5}
          />
        </div>
      </div>
    </div>
  );
}
